import React, { useState, useEffect, useMemo } from 'react';
import { Settings, X, Search, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/authStore';
import OsScroll from './ui/OsScroll';
import { getAppVersion } from '../services/updateService';
import { describeUpdateCheckResult } from '../utils/updateUtils';
import { isTauri } from '../utils/tauri';
import ReportIssueModal from './ReportIssueModal';
import { useExtensionStore } from '../extensions/store';
import AiChatSettings from './settings/AiChatSettings';
import McpSettings from './settings/McpSettings';
import ProxySettings from './ProxySettings';
import RequestsSettings from './settings/RequestsSettings';
import CertificatesSettings from './settings/CertificatesSettings';
import { resolveExtensionIcon } from './ActivityBar';
import { SETTINGS_SEARCH_KEYWORDS, TABS, TAB_SUBTITLE } from './settings/settingsTabs';
import { GeneralTab } from './settings/GeneralTab';
import { AppearanceTab } from './settings/AppearanceTab';
import { EditorTab } from './settings/EditorTab';
import { NotificationsTab } from './settings/NotificationsTab';
import { AccountTab } from './settings/AccountTab';
import { BillingTab } from './settings/BillingTab';
import { KeyboardTab } from './settings/KeyboardTab';
import { AboutTab } from './settings/AboutTab';
import { ExtensionSettingsPage, ExtensionsTab } from './settings/ExtensionsTab';
import { ChatTab } from './settings/ChatTab';
import { PrivacyTab } from './settings/PrivacyTab';
export { BillingTab } from './settings/BillingTab';
export { InstalledExtensionCard } from './settings/InstalledExtensionCard';

/* ── Main SettingsModal ──────────────────────────────────────────────────────── */
const SettingsModal = ({ isOpen, onClose, onCheckForUpdates, initialTab, showToast }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(initialTab || 'general');
  const [appVersion, setAppVersion]           = useState('');
  const [checkingUpdates, setCheckingUpdates] = useState(false);
  const [checkFeedback, setCheckFeedback]     = useState(null);
  const [reportOpen, setReportOpen]           = useState(false);
  const desktopApp = isTauri();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const extensionsMap = useExtensionStore((s) => s.extensions);
  const extensionsEnabled = useExtensionStore((s) => s.enabled);

  // Filter tabs: auth-only tabs are hidden when not signed in.
  const tabs = useMemo(
    () => (isAuthenticated ? TABS : TABS.filter((tab) => !tab.authOnly)),
    [isAuthenticated],
  );

  // Sub-items under Extensions: one per enabled extension, tab id
  // `extensions:<extensionId>`.
  const enabledExtensions = useMemo(
    () =>
      Object.values(extensionsMap)
        .filter((m) => extensionsEnabled[m.id])
        .sort((a, b) => a.name.localeCompare(b.name)),
    [extensionsMap, extensionsEnabled],
  );
  const activeExtensionId = activeTab.startsWith('extensions:')
    ? activeTab.slice('extensions:'.length)
    : null;
  const activeExtension = activeExtensionId ? extensionsMap[activeExtensionId] : null;

  // Extensions sub-items are collapsible; auto-expand while anything in the
  // extensions family is the active tab so the highlighted item stays visible.
  const [extensionsExpanded, setExtensionsExpanded] = useState(false);
  useEffect(() => {
    if (activeTab === 'extensions' || activeExtensionId) setExtensionsExpanded(true);
  }, [activeTab, activeExtensionId]);

  // Sidebar filter: label/subtitle are matched via i18n, plus the
  // SETTINGS_SEARCH_KEYWORDS extras; the first keyword hit renders as a
  // sub-line under the tab name. null = not searching.
  const [navQuery, setNavQuery] = useState('');
  const navMatches = useMemo(() => {
    const q = navQuery.trim().toLowerCase();
    if (!q) return null;
    const m = new Map();
    for (const tab of tabs) {
      const label = t(tab.labelKey) || '';
      if (label.toLowerCase().includes(q)) {
        m.set(tab.id, { hit: null });
        continue;
      }
      const kw = (SETTINGS_SEARCH_KEYWORDS[tab.id] || []).find((k) =>
        k.toLowerCase().includes(q)
      );
      if (kw) {
        m.set(tab.id, { hit: kw });
        continue;
      }
      const subtitle = TAB_SUBTITLE[tab.id] ? t(TAB_SUBTITLE[tab.id]) : '';
      if (subtitle && subtitle.toLowerCase().includes(q)) m.set(tab.id, { hit: subtitle });
    }
    return m;
  }, [navQuery, tabs, t]);
  const extensionHits = useMemo(() => {
    const q = navQuery.trim().toLowerCase();
    if (!q) return null;
    return enabledExtensions.filter((ext) => ext.name.toLowerCase().includes(q));
  }, [navQuery, enabledExtensions]);

  useEffect(() => {
    if (!isOpen) setNavQuery('');
  }, [isOpen]);

  // Uninstalled (from anywhere) while its page is open — fall back to overview.
  useEffect(() => {
    if (activeExtensionId && !extensionsMap[activeExtensionId]) setActiveTab('extensions');
  }, [activeExtensionId, extensionsMap]);

  // When modal opens (or initialTab changes), navigate to the requested tab.
  useEffect(() => {
    if (isOpen) setActiveTab(initialTab || 'general');
  }, [isOpen, initialTab]);

  // If the user signs out while on an auth-only tab, fall back to General.
  useEffect(() => {
    if (!isAuthenticated && TABS.find((t) => t.id === activeTab)?.authOnly) {
      setActiveTab('general');
    }
  }, [isAuthenticated, activeTab]);

  useEffect(() => {
    if (!isOpen) { setCheckFeedback(null); return; }
    let cancelled = false;
    getAppVersion().then((v) => { if (!cancelled) setAppVersion(v || '—'); });
    return () => { cancelled = true; };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const handleCheckForUpdates = async () => {
    if (!onCheckForUpdates || checkingUpdates) return;
    setCheckingUpdates(true);
    setCheckFeedback(null);
    try {
      const result = await onCheckForUpdates();
      if (result?.currentVersion) setAppVersion(result.currentVersion);
      setCheckFeedback(describeUpdateCheckResult(result, t));
    } catch {
      setCheckFeedback({ variant: 'error', message: t('settings.checkFailed') });
    } finally {
      setCheckingUpdates(false);
    }
  };

  if (!isOpen) return null;

  const activeTabMeta = tabs.find((tab) => tab.id === activeTab);

  return (
    <div className="settings-dialog-overlay" onClick={onClose}>
      <div className="settings-dialog" onClick={(e) => e.stopPropagation()}>

        {/* ── Left sidebar ── */}
        <div className="settings-sidebar">
          <div className="settings-sidebar-header">
            <Settings size={15} aria-hidden />
            {t('settings.title')}
          </div>

          <div className="settings-nav-search">
            <Search size={13} aria-hidden />
            <input
              type="text"
              value={navQuery}
              onChange={(e) => setNavQuery(e.target.value)}
              placeholder={t('settings.searchPlaceholder', 'Search settings')}
              aria-label={t('settings.searchPlaceholder', 'Search settings')}
              spellCheck={false}
            />
          </div>

          {/* OsScroll, not native overflow-y: macOS WKWebView ignores
              ::-webkit-scrollbar styling whenever the system uses overlay
              scrollbars, so the thin 4px bar reverted to the wide native one
              (reported with a screenshot of exactly that). The nav's own flex
              column lives on the viewport element in CSS, since
              OverlayScrollbars moves these children into it. */}
          <OsScroll element="nav" className="settings-nav" aria-label={t('settings.title')}>
            {tabs.map(({ id, Icon, labelKey }) => {
              const searching = navMatches != null;
              const match = searching ? navMatches.get(id) : null;
              const extSubs =
                id === 'extensions'
                  ? searching
                    ? extensionHits
                    : extensionsExpanded
                      ? enabledExtensions
                      : []
                  : [];
              if (searching && !match && extSubs.length === 0) return null;
              return (
                <React.Fragment key={id}>
                  <button
                    type="button"
                    onClick={() => {
                      if (!searching && id === 'extensions' && activeTab === 'extensions') {
                        // Second click on the already-active entry toggles the fold.
                        setExtensionsExpanded((e) => !e);
                        return;
                      }
                      setActiveTab(id);
                    }}
                    className={`settings-nav-item${activeTab === id ? ' settings-nav-item--active' : ''}`}
                    aria-current={activeTab === id ? 'page' : undefined}
                    aria-expanded={!searching && id === 'extensions' ? extensionsExpanded : undefined}
                  >
                    <Icon size={15} aria-hidden />
                    <span className="settings-nav-texts">
                      <span>{t(labelKey)}</span>
                      {match?.hit && <span className="settings-nav-match">{match.hit}</span>}
                    </span>
                    {!searching && id === 'extensions' && enabledExtensions.length > 0 && (
                      <ChevronRight
                        size={13}
                        aria-hidden
                        className="settings-nav-chevron"
                        style={extensionsExpanded ? { transform: 'rotate(90deg)' } : undefined}
                      />
                    )}
                  </button>
                  {extSubs.map((ext) => {
                    const subId = `extensions:${ext.id}`;
                    const SubIcon = resolveExtensionIcon(ext.contributes?.activityBar?.[0]?.icon);
                    return (
                      <button
                        key={subId}
                        type="button"
                        onClick={() => setActiveTab(subId)}
                        className={`settings-nav-item settings-nav-subitem${
                          activeTab === subId ? ' settings-nav-item--active' : ''
                        }`}
                        aria-current={activeTab === subId ? 'page' : undefined}
                      >
                        <SubIcon size={13} aria-hidden />
                        {ext.name}
                      </button>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </OsScroll>

          <div className="settings-sidebar-footer">
            APIBOO · {appVersion || '…'}
          </div>
        </div>

        {/* ── Right panel ── */}
        <div className="settings-panel">
          <div className="settings-panel-header">
            <div>
              <div className="settings-panel-title">
                {activeExtension ? activeExtension.name : t(activeTabMeta?.labelKey)}
              </div>
              <div className="settings-panel-subtitle">
                {activeExtension
                  ? t('settings.subtitleExtensionSettings', 'Settings and permissions for this extension.')
                  : t(TAB_SUBTITLE[activeTab])}
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="settings-panel-close"
              aria-label={t('common.close')}
            >
              <X size={16} />
            </button>
          </div>

          {/* Same reason as .settings-nav above. */}
          <OsScroll className="settings-panel-body">
            {activeTab === 'general' && (
              <GeneralTab
                t={t}
                appVersion={appVersion}
                desktopApp={desktopApp}
                checkingUpdates={checkingUpdates}
                checkFeedback={checkFeedback}
                onCheckForUpdates={handleCheckForUpdates}
              />
            )}
            {activeTab === 'appearance'   && <AppearanceTab t={t} />}
            {activeTab === 'editor'       && <EditorTab t={t} />}
            {activeTab === 'requests'     && <RequestsSettings t={t} desktopApp={desktopApp} />}
            {activeTab === 'proxy'        && <ProxySettings />}
            {activeTab === 'notifications' && <NotificationsTab t={t} desktopApp={desktopApp} />}
            {activeTab === 'chat'         && <ChatTab t={t} />}
            {activeTab === 'aiChat'       && <AiChatSettings t={t} />}
            {activeTab === 'account'      && <AccountTab t={t} showToast={showToast} />}
            {activeTab === 'billing'      && <BillingTab showToast={showToast} />}
            {activeTab === 'keyboard'     && <KeyboardTab t={t} />}
            {activeTab === 'certificates' && <CertificatesSettings />}
            {activeTab === 'mcp'          && <McpSettings t={t} />}
            {activeTab === 'privacy'      && <PrivacyTab t={t} />}
            {activeTab === 'extensions'   && <ExtensionsTab />}
            {activeExtension && (
              <ExtensionSettingsPage
                extensionId={activeExtensionId}
                t={t}
                onUninstalled={() => setActiveTab('extensions')}
              />
            )}
            {activeTab === 'about'        && (
              <AboutTab t={t} appVersion={appVersion} onReportIssue={() => setReportOpen(true)} />
            )}
          </OsScroll>
        </div>

      </div>

      {/* Sibling of the settings surface, not a child of the About tab: the tab
          unmounts when the user switches away, and a modal that vanishes
          mid-typing because of a background click would lose the report. */}
      <ReportIssueModal
        isOpen={reportOpen}
        onClose={() => setReportOpen(false)}
        showToast={showToast}
      />
    </div>
  );
};

export default SettingsModal;
